import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classnames from 'classnames';
import Axis from '../Axis/index.js';
import { VictoryChart, VictoryBar, VictoryGroup, VictoryTooltip, VictoryLabel, VictoryContainer } from 'victory';
import sizes from '../sizes.js';
import colors from '../../../utils/colors.js';
import { useMaturityLevel } from '../../../component-maturity.js';

const parse = ({ data, empty, isEmpty }) => {
  if (isEmpty) {
    return [[{
      label: empty,
      xIndex: 'x-0',
      value: 1,
      tooltip: empty,
      color: colors.grey200
    }]];
  }
  const withNumericValues = _.map(data, (entries) => _.map(entries, (entry) => (
    _.merge({}, entry, { value: +_.get(entry, 'value') || 0 })
  )));
  const hasGroups = withNumericValues.length > 1;
  const withColors = _.map(withNumericValues, (entries, groupIndex) => _.map(entries, (entry, entryIndex) => {
    const indexForColor = hasGroups ? groupIndex : entryIndex;
    return _.merge({}, entry, {
      xIndex: `x-${entryIndex}`,
      entryIndex,
      groupIndex,
      color: entry.color || _.get(colors.order, indexForColor % colors.order.length, 'black')
    });
  }));
  return withColors;
};

const tickValuesFor = (data, isEmpty) => {
  const tickValuesForX = _.uniq(_.flatMap(data, entries => _.map(entries, 'xIndex')));
  if (isEmpty) {
    return { x: tickValuesForX, y: [1] };
  }
  // if left as numbers, Victory assumes your data in X is continuous
  const tickValuesForY = _.uniq(_.orderBy(_.flatMap(data, entries => _.map(entries, 'value'))));
  return { x: tickValuesForX, y: tickValuesForY };
};

const tickFormatsFor = (data, isEmpty, empty, format) => {
  const tickFormatForX = (xIndex) => {
    const entryIndex = xIndex.replace(/^x-/, '');
    return _.get(_.get(data, [0, entryIndex]), 'label') || _.method('x', entryIndex)(format);
  };
  if (isEmpty) {
    return { x: tickFormatForX, y: () => empty };
  }
  return { x: tickFormatForX, y: format.y };
};

const calculateDomainPadding = ({
  numberOfTicks,
  numberOfBarsPerTick,
  barWidth,
  width
}) => {
  const numberOfGutters = numberOfTicks - 1;
  const widthOfGuttersCombined = barWidth * numberOfGutters;
  const widthOfBarsCombined = barWidth * numberOfBarsPerTick * numberOfTicks;
  const totalWidthCombined = widthOfBarsCombined + widthOfGuttersCombined;
  const padding = Math.floor((width - totalWidthCombined) / 2);
  return padding + ((numberOfTicks % 2) * barWidth);
};

const calculateBarWidth = ({
  numberOfTicks,
  numberOfBarsPerTick,
  width,
  maxBarWidth,
  minBarWidth,
  paddingBars
}) => {
  const numberOfGutters = numberOfTicks - 1;
  const numberOfBarsCombined = numberOfGutters + (numberOfBarsPerTick * numberOfTicks) + paddingBars;
  const barWidth = Math.floor(width / numberOfBarsCombined);
  return _.clamp(barWidth, minBarWidth, maxBarWidth);
};

// this is needed because of how victory calculates layout
const hardcodedTooltip = { fontSize: 9 };
const bars = ({
  parsed,
  tooltipClasses,
  openTooltips,
  lineHeight
}) => _.map(parsed, (entries, index) => (
  <VictoryBar
    containerComponent={<VictoryContainer responsive={false}/>}
    key={`bar-chart-bar-${index}`}
    data={entries}
    x={({ xIndex }) => (xIndex)}
    y={({ value }) => (+value || 0)}
    style={{
      data: {
        fill: ({ color } = {}) => color
      }
    }}
    labelComponent={
      <VictoryTooltip
        active={openTooltips}
        text={({ tooltip, value }) => (tooltip || value)}
        labelComponent={(
          <VictoryLabel style={hardcodedTooltip} lineHeight={lineHeight} className={tooltipClasses} />
        )}
        flyoutStyle={{ stroke: ({ color } = {}) => color, fill: 'white' }}
      />
    }
  />
));

const axis = ({
  axisLabelPadding,
  tickValues,
  tickFormats,
  isEmpty,
  label
}) => {
  return [
    Axis.X({
      padding: axisLabelPadding.x,
      tickValues: tickValues.x,
      tickFormats: tickFormats.x,
      isEmpty: isEmpty,
      label: label.x
    }),
    Axis.Y({
      padding: axisLabelPadding.y,
      tickValues: tickValues.y,
      tickFormats: tickFormats.y,
      isEmpty: isEmpty,
      label: label.y
    })
  ];
};

/** Render a bar chart */
const Bar = ({
  size,
  openTooltips,
  className,
  data,
  empty,
  width,
  height,
  domainPaddingY,
  lineHeight,
  label,
  axisLabelPadding,
  format,
  ...barWidthProps
}) => {
  useMaturityLevel('deprecated', 'Bar');
  const sizedWidth = width || sizes.width(size);
  const sizedHeight = height || sizes.height(size);
  const isEmpty = !_.some(data) || !_.some(data, entries => _.some(entries));
  const parsed = parse({ data, empty, isEmpty });
  const classes = classnames('particles-bar-chart', className, {
    'particles-bar-chart--empty': !!isEmpty
  });
  const tooltipClasses = classnames('particles-bar-chart__tooltip', {
    'particles-bar-chart__tooltip--empty': !!isEmpty
  });
  const tickValues = tickValuesFor(parsed, isEmpty, empty);
  const tickFormats = tickFormatsFor(parsed, isEmpty, empty, format);
  const numberOfTicks = tickValues.x.length;
  const numberOfBarsPerTick = parsed.length;
  const barWidth = calculateBarWidth({ numberOfTicks, numberOfBarsPerTick, width: sizedWidth, ...barWidthProps });
  const domainPaddingX = calculateDomainPadding({ numberOfTicks, numberOfBarsPerTick, width: sizedWidth, barWidth });

  return (
    <div className={classes}>
      <VictoryChart
        width={sizedWidth}
        height={sizedHeight}
        domainPadding={{ x: domainPaddingX, y: [0, domainPaddingY] }}
        containerComponent={<VictoryContainer responsive={false} style={{ overflow: 'visible' }} />}
        style={{ parent: { overflow: 'visible' } }}
      >
        {axis({ tickValues, tickFormats, isEmpty, label, axisLabelPadding })}
        <VictoryGroup offset={barWidth} style={{ data: { width: barWidth } }}>
          {bars({ parsed, lineHeight, tooltipClasses, openTooltips })}
        </VictoryGroup>
      </VictoryChart>
    </div>
  );
};

Bar.propTypes = {
  /** Array of data to display in chart. Each item in the array can have the following keys
   * "label", "color", "value", "tooltip".
  */
  data: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    color: PropTypes.oneOfType([PropTypes.string]),
    label: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    tooltip: PropTypes.oneOfType([PropTypes.array, PropTypes.string])
  }))).isRequired,
  /** Padding around axis text labels */
  axisLabelPadding: PropTypes.shape({
    x: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    y: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  /** Axis value display formatter. You can pass a function for both x and y axis values. Your custom
   * function is passed an axis value and should return a string.
   * ie: `(value) => string`
   */
  format: PropTypes.shape({
    x: PropTypes.func,
    y: PropTypes.func
  }),
  /** Size of the chart, ['small', 'medium', 'large'] */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /** Placeholder text to show when chart has no data */
  empty: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /** Specifies labels to display for x or y axis */
  label: PropTypes.shape({ x: PropTypes.string, y: PropTypes.string }),
  /** Additional class to add to component */
  className: PropTypes.string,
  /** Toggle display of tooltips */
  openTooltips: PropTypes.bool,
  /** Padding between bars. NOTE: Not recommended to modify in normal circumstances */
  paddingBars: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Minimun width for bars. NOTE: Not recommended to modify in normal circumstances */
  minBarWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Maximun width for bars. NOTE: Not recommended to modify in normal circumstances */
  maxBarWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Number of pixels of padding to add the beginning or end of a domain. This prop is useful for explicitly spacing data elements farther from the beginning or end of a domain to prevent axis crowding. NOTE: Not recommended to modify in normal circumstances */
  domainPaddingY: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Specify a static width in pixels of chart. NOTE: Not recommended to modify in normal circumstances */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Specify a static height in pixels of charts. NOTE: Not recommended to modify in normal circumstances */
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Line height for text in the chart. NOTE: Not recommended to modify in normal circumstances */
  lineHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

Bar.defaultProps = {
  axisLabelPadding: {
    x: 40,
    y: 50
  },
  format: {
    x: _.identity,
    y: _.identity
  },
  size: 'medium',
  className: '',
  label: { x: '', y: '' },
  openTooltips: false,
  paddingBars: 3,
  minBarWidth: 5,
  maxBarWidth: 60,
  domainPaddingY: 10,
  lineHeight: 2,
  empty: 'No Data',
  data: [[]]
};

Bar.displayName = 'Chart.Bar';

export default Bar;

export const {
  propTypes,
  defaultProps,
  displayName
} = Bar;
